import React, { PropsWithChildren } from "react";
import { useLocation } from "react-router";
import Sidebar from "./Sidebar";
import Searchbar from "./Searchbar";
import ToastRegion from "../DesignSystem/Toast/ToastRegion";
// The design-system role tokens (--ds-*). The shell loads them for every authed
// screen; the Tailwind classes carry the same values as var() fallbacks, so nothing
// breaks where this import never ran. See tokens.css for the theming contract.
import "../DesignSystem/tokens.css";
import { useThemePreference } from "../DesignSystem/theme";

const Layout = (props: PropsWithChildren<{}>) => {
  const location = useLocation();
  /*
    The shell owns the colour-scheme preference — it is the one thing mounted for
    every authed route, and it already loads the tokens the preference switches
    between. `Searchbar` only FORWARDS it to the profile menu, where the control
    lives; when a second consumer appears (a Settings screen, say), this is the point
    to lift into a context rather than drill a third level.

    **No `allowDark` any more.** It used to carry the other half of the dark-theme role
    gate: hiding the profile-menu control was not a gate by itself, because the default
    preference is `system`, so a non-admin with a dark OS got `data-theme="dark"` anyway.
    Both halves are gone now — theme switching is available to every signed-in user, so
    `system` resolves to the user's real OS preference for everyone.

    `useThemePreference` still ACCEPTS `allowDark` (defaulting true), so re-gating is one
    argument away if dark is ever restricted again. It is simply nobody's business here.

    Signin renders outside this shell and therefore stays light, which is deliberate:
    `styles.css` is written so those screens render correctly without tokens.css.
  */
  const { preference: themePreference, setPreference: setThemePreference } = useThemePreference();
  /*
    `h-screen` + `overflow-clip` on the root make the shell fail CLOSED — see the row
    comment below for the incident. With a definite height HERE, the row's `flex-grow`
    re-derives its size even if its own height class is lost, and anything that still
    outgrows 100vh is clipped instead of leaking into the document scroller. When
    healthy this is pixel-identical: 60px header + calc(100vh-60px) row = 100vh.
    Overlays are unaffected — Dialog/Toast portal to <body>; position:fixed content
    rendered into the portals below escapes ancestor clipping by definition.
  */
  return (
    /* `bg-surface` on the shell root. `<main>` is transparent by design (see the note
       on the header's stacking context), so without a ground HERE every page composited
       straight onto the browser's own white — which is what left the content area white
       under a dark sidebar. */
    <div className={`tw-w-screen tw-h-screen tw-overflow-clip tw-flex tw-flex-col tw-relative tw-bg-surface tw-font-noto-sans relative`} data-page={location.pathname}>
      {/* top */}
      {/* `shrink-0`: in the degraded state the row's comment describes, flex-shrink
          pressure would squash this header toward its content height. No effect when
          healthy — 60 + calc(100vh-60px) carries no shrink pressure.

          `relative` is what makes the `z-50` beside it mean anything. `z-index` applies
          only to positioned elements, so on a static box it was inert — and the header's
          menus (Create, profile) hang BELOW the 60px header, over the content row, which
          is `relative z-10` and therefore painted after it. The menus looked fine only
          because <main> happens to be transparent where they land; the first page with a
          background there would have covered them. One word, and the header now owns a
          stacking context that outranks the row it overhangs. Nothing moves: `relative`
          with no offsets changes no geometry, and the sticky <nav> inside resolves
          against the same scrollport as before. */}
      {/* `border-edge`: a bare `tw-border` takes `borderColor.DEFAULT` (#DFE0E6), which
          has no dark value and drew a light hairline across a dark shell. */}
      <div className={`tw-h-[60px] tw-shrink-0 tw-border tw-border-edge tw-relative tw-z-50`}>
        <Searchbar
          themePreference={themePreference}
          onThemePreferenceChange={setThemePreference}
        />
      </div>

      {/* bottom */}
      {/*
        `overflow-clip` makes the shell fail CLOSED. This row is the app's height
        boundary — sidebar and <main> scroll internally — but the row itself carried no
        clipping, so ANY failure that let <main> outgrow it (measured live 2026-08-25: a
        dev tab holding stale Tailwind CSS after a tailwind.config.js rebuild dropped
        `tw-h-[calc(100vh-60px)]`, and the entire page leaked into the document
        scroller — 6,382px of scroll on a 401px viewport) turned into a scrolling void
        below the app. Clip on this row alone was measured INSUFFICIENT for that
        incident (the row itself grows in-flow when its height rule dies), which is why
        the root above carries `h-screen` + `overflow-clip`: flex-grow + `min-h-0` here
        re-derive this row's size from the root, and the root clips whatever remains.
        Verified in headless Chromium with the height class deleted: document stays at
        exactly viewport height and <main> keeps scrolling internally.

        `clip`, not `hidden`: an overflow-hidden box is still a programmatic scroll
        container, so a focus()/scrollIntoView() deep in <main> could scroll THIS row
        and shove the header out of view with no scrollbar to bring it back. `clip`
        cannot be scrolled by anything. Overlays are unaffected — Dialog/Toast portal
        to <body>, Dropdown/Tooltip portal out when inside clipping containers.
      */}
      <div className={`tw-h-[calc(100vh-60px)] tw-min-h-0 tw-flex-grow tw-flex tw-relative tw-z-10 tw-overflow-clip`}>
        <div className={`tw-max-h-full tw-overflow-auto tw-min-w-fit`}>
          <Sidebar />
        </div>
        <main className={`tw-max-h-full tw-max-w-full tw-overflow-auto tw-flex-grow tw-pt-3 tw-px-4 `}>
          <div className={`tw-w-full tw-min-h-full tw-flex tw-flex-col`}>{props.children}</div>
        </main>
      </div>
      <div id="tooltip-portal"></div>
      <div id="sidepanel-portal" className ="tw-z-[2030]"/>
      {/*
        Mounted ONCE, here, for the whole app. `toast()` is callable from any module —
        including non-components like axios interceptors — but it needs exactly one
        viewport listening to the store.

        It must not be conditional. `ToastRegion` keeps two always-present `aria-live`
        regions, and a live region has to exist BEFORE text lands in it or the screen
        reader has nothing subscribed and the first toast of the session is silent.
        See `okf/design-system/e2e.md` § 41.1.

        It portals to `document.body` itself, so this position is for legibility next
        to the other portal roots rather than for stacking — its own z-index (2040)
        clears `#sidepanel-portal` above.
      */}
      <ToastRegion />
    </div>
  );
}

export default Layout;
